import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import { SkeletonCard } from "@/components/base/Skeleton";
import {
  listSessions,
  listDebateRounds,
  listAnalystViews,
  type DebateRound,
  type AnalystView,
} from "@/api/alpha";

function ConfidenceStars({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`text-xs ${s <= value ? "ri-star-fill text-amber-400" : "ri-star-line text-foreground-300"}`}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Mapping helpers — translate the backend DebateRound / AnalystView shapes
//  into the view-model the original mock provided. Field names and number
//  formats (1-5 confidence, ¥ prices, % positions) match the JSX below.
// ---------------------------------------------------------------------------

// Tolerant JSON parse: backend stores some columns as a JSON string, and a
// few LLM-written blobs wrap the JSON in <reasoning> tags or prose. Try a
// straight parse first, then fall back to the first {…}/[…] substring.
function parseJSON<T>(s?: string): T | null {
  if (!s) return null;
  // Backend may return already-parsed objects instead of JSON strings
  if (typeof s !== 'string') return s as T;
  try {
    return JSON.parse(s) as T;
  } catch {
    /* fall through */
  }
  const m = s.match(/[{[][\s\S]*[}\]]/);
  if (m) {
    try {
      return JSON.parse(m[0]) as T;
    } catch {
      /* ignore */
    }
  }
  return null;
}

// expectedPrice / stopLoss / takeProfit come back as free-form strings
// ("205.00", "¥205", "205元附近"). Strip to the leading number. Returns
// null when the column is empty / unparseable so the UI can render "—"
// instead of a misleading ¥0.00.
function parsePrice(s?: string): number | null {
  if (!s) return null;
  const cleaned = String(s).replace(/[^\d.]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

function fmtPrice(n: number | null): string {
  return n == null ? "—" : `¥${n.toFixed(2)}`;
}

function actionTypeOf(action: string): "buy" | "sell" | "hold" {
  if (/买|增|加/.test(action)) return "buy";
  if (/卖|减|清/.test(action)) return "sell";
  return "hold";
}

const clampStars = (n?: number) => Math.max(0, Math.min(5, Math.round(n || 0)));

// Classify an analyst's real verdict label into a sentiment tone — used ONLY
// to pick a badge colour for the (real) verdict text. No fabricated numeric
// score is shown; the backend exposes no 0-100 per-perspective rating.
function verdictTone(v: string): "bull" | "bear" | "neutral" {
  const s = v || "";
  if (/强买|超配|利好|看多|乐观|强烈|领先|向好|买入|偏多|多头|积极|增持|优于|认可/.test(s))
    return "bull";
  if (/减持|偏空|谨慎|回避|减配|承压|疲弱|卖出|看空|利空|悲观|空头|风险/.test(s))
    return "bear";
  return "neutral";
}

const AGENT_META: Record<string, { label: string; icon: string }> = {
  fundamental: { label: "基本面", icon: "ri-bar-chart-2-line" },
  technical: { label: "技术面", icon: "ri-line-chart-line" },
  sentiment: { label: "情绪面", icon: "ri-fire-line" },
  news: { label: "新闻面", icon: "ri-newspaper-line" },
};
const AGENT_ORDER = ["fundamental", "technical", "sentiment", "news"];

const STANCE_META = [
  { key: "aggressive", label: "激进" },
  { key: "neutral", label: "中性" },
  { key: "conservative", label: "保守" },
] as const;

interface Perspective {
  label: string;
  icon: string;
  verdict: string;
  tone: "bull" | "bear" | "neutral";
  summary: string;
  detail: string;
}
interface RiskView {
  label: string;
  positionSuggestion: string;
  concern: string;
}
interface DecisionVM {
  stockName: string;
  stockCode: string;
  action: string;
  actionType: "buy" | "sell" | "hold";
  confidence: number;
  suggestedPosition: number;
  simulatedTargetPrice: number | null;
  stopLossPrice: number | null;
  stopProfitPrice: number | null;
  time: string;
  perspectives: Perspective[];
  bullBearDebate: {
    bull: { title: string; confidence: number; points: string[] };
    bear: { title: string; confidence: number; points: string[] };
  };
  riskViews: RiskView[];
}

function buildPerspectives(analysts: AnalystView[]): Perspective[] {
  const out: Perspective[] = [];
  for (const t of AGENT_ORDER) {
    const v = analysts.find((a) => a.agentType === t);
    if (!v) continue;
    const meta = AGENT_META[t];
    const metrics =
      parseJSON<{ label: string; value: string }[]>(v.metrics) || [];
    const summary = metrics
      .slice(0, 3)
      .map((m) => `${m.label} ${m.value}`)
      .join(" · ");
    out.push({
      label: meta.label,
      icon: meta.icon,
      verdict: v.verdict || "",
      tone: verdictTone(v.verdict),
      summary,
      detail: v.currentMessage || "",
    });
  }
  return out;
}

function buildRiskViews(round: DebateRound): RiskView[] {
  const stances = parseJSON<Record<string, string>>(round.riskStances) || {};
  const out: RiskView[] = [];
  for (const s of STANCE_META) {
    const raw = stances[s.key];
    if (!raw) continue;
    // A stance blob is either a JSON object { concern, positionSuggestion }
    // or, on older rows, a plain prose string (treated as the concern).
    const parsed = parseJSON<{
      concern?: string;
      positionSuggestion?: string;
    }>(raw);
    out.push({
      label: s.label,
      positionSuggestion: parsed?.positionSuggestion || "",
      concern: parsed?.concern || (parsed ? "" : raw),
    });
  }
  return out;
}

function buildVM(round: DebateRound, analysts: AnalystView[]): DecisionVM {
  const d = new Date(round.createdAt * 1000);
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}`;
  const bullPts = parseJSON<{ text: string }[]>(round.bullPoints) || [];
  const bearPts = parseJSON<{ text: string }[]>(round.bearPoints) || [];
  return {
    stockName: round.ticker,
    stockCode: round.tickerCode,
    action: round.finalAction,
    actionType: actionTypeOf(round.finalAction),
    confidence: clampStars(round.confidence),
    suggestedPosition: round.suggestedPosition || 0,
    simulatedTargetPrice: parsePrice(round.expectedPrice),
    stopLossPrice: parsePrice(round.stopLoss),
    stopProfitPrice: parsePrice(round.takeProfit),
    time,
    perspectives: buildPerspectives(analysts),
    bullBearDebate: {
      bull: {
        title: "看多观点",
        confidence: clampStars(round.bullConfidence),
        points: bullPts.map((p) => p.text).filter(Boolean),
      },
      bear: {
        title: "看空观点",
        confidence: clampStars(round.bearConfidence),
        points: bearPts.map((p) => p.text).filter(Boolean),
      },
    },
    riskViews: buildRiskViews(round),
  };
}

export default function DecisionDetail() {
  const { id } = useParams();
  const [data, setData] = useState<DecisionVM | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [empty, setEmpty] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setEmpty("");
      try {
        const { sessions } = await listSessions();
        const s =
          sessions.find((x) => x.status === "running") || sessions[0];
        if (!s) {
          if (!cancelled) setEmpty("请先在 AI 看盘开启模拟盘");
          return;
        }
        const { rounds } = await listDebateRounds(s.id);
        if (!rounds || rounds.length === 0) {
          if (!cancelled) setEmpty("暂无 AI 决策记录");
          return;
        }
        // Backend returns rounds oldest-first; reverse so index 0 = latest,
        // matching the dashboard's "最新研判在前" ordering. The route :id is
        // that list index.
        const ordered = rounds.slice().reverse();
        const idx = Number(id);
        const round =
          ordered[Number.isFinite(idx) ? idx : 0] || ordered[0];
        const { analysts } = await listAnalystViews(s.id, round.id);
        if (!cancelled) setData(buildVM(round, analysts || []));
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "加载失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="pb-[80px]">
        <PageHeader title="AI 决策详情" backTo="/" />
        <div className="px-4 space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (error || empty || !data) {
    return (
      <div className="pb-[80px]">
        <PageHeader title="AI 决策详情" backTo="/" />
        <div className="px-4 mt-20 flex flex-col items-center text-center">
          <i className="ri-inbox-line text-foreground-300 text-4xl mb-3" />
          <p className="text-sm text-foreground-500">
            {error || empty || "暂无数据"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-[80px]">
      <PageHeader title="AI 决策详情" backTo="/" />

      {/* Conclusion Header */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-lg font-bold text-foreground-950">{data.stockName}</h1>
                  <span className="text-sm text-foreground-400">{data.stockCode}</span>
                </div>
                <p className="text-xs text-foreground-400">{data.time}</p>
              </div>
            </div>
            <span className={`text-sm font-bold px-3 py-1.5 rounded-full ${
              data.actionType === "buy"
                ? "bg-[#E11D48]/10 text-[#E11D48]"
                : data.actionType === "sell"
                ? "bg-[#10B981]/10 text-[#10B981]"
                : "bg-primary-100 text-primary-700"
            }`}>
              {data.action}
            </span>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <ConfidenceStars value={data.confidence} />
            <span className="text-xs text-foreground-500">置信度 · {data.confidence}/5</span>
          </div>

          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span>模拟 · 仅供学习参考</span>
          </div>
        </div>
      </div>

      {/* 4 Perspectives */}
      {data.perspectives.length > 0 && (
        <div className="px-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">四维度分析</h3>
          <div className="space-y-3">
            {data.perspectives.map((p) => (
              <div key={p.label} className="bg-white rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                    <i className={`${p.icon} text-foreground-600 text-base`} />
                  </div>
                  <span className="text-sm font-semibold text-foreground-950">{p.label}</span>
                  {p.verdict && (
                    <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
                      p.tone === "bull"
                        ? "bg-[#E11D48]/10 text-[#E11D48]"
                        : p.tone === "bear"
                        ? "bg-[#10B981]/10 text-[#10B981]"
                        : "bg-background-100 text-foreground-600"
                    }`}>
                      {p.verdict}
                    </span>
                  )}
                </div>

                {p.summary && (
                  <p className="text-xs text-foreground-500 mb-2">{p.summary}</p>
                )}
                {p.detail && (
                  <p className="text-sm text-foreground-700 leading-relaxed">{p.detail}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bull vs Bear */}
      {(data.bullBearDebate.bull.points.length > 0 ||
        data.bullBearDebate.bear.points.length > 0) && (
        <div className="px-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">多空辩论</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-4 border-t-2 border-[#E11D48]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#E11D48]">🔴 {data.bullBearDebate.bull.title}</p>
                {data.bullBearDebate.bull.confidence > 0 && (
                  <ConfidenceStars value={data.bullBearDebate.bull.confidence} />
                )}
              </div>
              {data.bullBearDebate.bull.points.map((text, i) => (
                <p key={i} className="text-xs text-foreground-700 leading-relaxed mb-2 last:mb-0">
                  {text}
                </p>
              ))}
            </div>

            <div className="bg-white rounded-xl p-4 border-t-2 border-[#10B981]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#10B981]">🟢 {data.bullBearDebate.bear.title}</p>
                {data.bullBearDebate.bear.confidence > 0 && (
                  <ConfidenceStars value={data.bullBearDebate.bear.confidence} />
                )}
              </div>
              {data.bullBearDebate.bear.points.map((text, i) => (
                <p key={i} className="text-xs text-foreground-700 leading-relaxed mb-2 last:mb-0">
                  {text}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Risk Views */}
      {data.riskViews.length > 0 && (
        <div className="px-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">风控三视角</h3>
          <div className="space-y-3">
            {data.riskViews.map((rv) => (
              <div key={rv.label} className="bg-white rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-sm font-bold ${
                    rv.label === "激进" ? "text-[#E11D48]" :
                    rv.label === "保守" ? "text-[#10B981]" :
                    "text-primary-600"
                  }`}>
                    {rv.label}
                  </span>
                  {rv.positionSuggestion && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">
                      {rv.positionSuggestion}
                    </span>
                  )}
                </div>

                {rv.concern && (
                  <p className="text-xs text-foreground-600 leading-relaxed">{rv.concern}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Final Decision */}
      <div className="px-4 mb-4">
        <h3 className="text-sm font-semibold text-foreground-950 mb-3">最终模拟决策</h3>
        <div className="bg-white rounded-xl p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-foreground-400">动作</p>
              <p className={`text-lg font-bold ${
                data.actionType === "buy" ? "text-[#E11D48]"
                : data.actionType === "sell" ? "text-[#10B981]"
                : "text-primary-700"
              }`}>{data.action}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-400">置信度</p>
              <ConfidenceStars value={data.confidence} />
            </div>
            <div>
              <p className="text-xs text-foreground-400">模拟目标价</p>
              <p className="text-sm font-semibold text-foreground-950">{fmtPrice(data.simulatedTargetPrice)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-400">止损 / 止盈</p>
              <p className="text-sm font-semibold">
                <span className="text-[#10B981]">{fmtPrice(data.stopLossPrice)}</span>
                <span className="text-foreground-400"> / </span>
                <span className="text-[#E11D48]">{fmtPrice(data.stopProfitPrice)}</span>
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-foreground-400">建议仓位%</p>
              <p className="text-lg font-bold text-foreground-950">{data.suggestedPosition}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="px-4 mb-4">
        <div className="bg-accent-50 rounded-xl p-3 flex items-start gap-2">
          <i className="ri-information-line text-accent-500 text-sm mt-0.5" />
          <p className="text-xs text-foreground-600 leading-relaxed">
            以上所有分析、评分、决策建议均为 AI 模拟生成，仅供学习参考，不构成任何投资建议。投资有风险，入市需谨慎。
          </p>
        </div>
      </div>
    </div>
  );
}
